import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@shared/components/ui/shadcn/avatar';
import { Checkbox } from '@shared/components/ui/checkbox';
import {
  TooltipContent,
  TooltipProvider,
  TooltipSH,
  TooltipTrigger,
} from '@shared/components/ui/shadcn/tooltip';
import { cn } from '@shared/utils/utils';
import { useState } from 'react';
import { Badge } from '../index';
import { ProductImageOverlay } from './ProductImageOverlay';

export interface ProductProps {
  acronym?: string;
  src: string;
  name: string;
  id: number;
  code?: string;
  className?: string;
  onSelect?: (id: number, selected: boolean) => void;
  'data-testid'?: string;
  /** Controla si se aplica line-clamp y funcionalidad de expansión */
  enableLineClamp?: boolean;
  /** Muestra tooltip con el nombre completo en hover */
  showTooltip?: boolean;
  /** Permite abrir la imagen en una modal al hacer clic */
  showImageModal?: boolean;
}

export const Product = ({
  id,
  acronym,
  src,
  name,
  code,
  onSelect,
  className,
  'data-testid': customTestId,
  enableLineClamp = true,
  showTooltip = false,
  showImageModal = false,
}: ProductProps) => {
  const dataTestId = customTestId ?? `product-${id}`;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isImageFloatingOpen, setIsImageFloatingOpen] = useState(false);

  const toggleExpansion = () => {
    if (enableLineClamp) {
      setIsExpanded(!isExpanded);
    }
  };

  const openImageFloating = () => {
    if (showImageModal && src) {
      setIsImageFloatingOpen(true);
    }
  };

  const closeImageFloating = () => {
    setIsImageFloatingOpen(false);
  };

  // 🎯 Contenido del nombre con lógica condicional
  const nameContent = (
    <div
      className={cn(
        'text-sm text-text',
        enableLineClamp && 'cursor-pointer hover:text-foreground/80',
        enableLineClamp && !isExpanded && 'line-clamp-1'
      )}
      onClick={enableLineClamp ? toggleExpansion : undefined}
      title={!showTooltip ? name || '' : undefined}
      data-testid={`${dataTestId}-name`}
    >
      {name || 'Sin nombre'}
    </div>
  );

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-2 w-fit transition-colors',
          className
        )}
        data-testid={dataTestId}
      >
        {onSelect && (
          <Checkbox
            onChange={(e) => {
              onSelect(id, (e.target as HTMLInputElement).checked);
            }}
            className="w-4 h-4 rounded-md border-border focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`${dataTestId}-checkbox`}
          />
        )}

        <div>
          <div className="flex items-center gap-2">
            <Avatar
              className={cn(
                'w-8 h-8',
                showImageModal &&
                  src &&
                  'cursor-pointer hover:opacity-80 transition-all duration-200 hover:scale-105'
              )}
              onClick={showImageModal ? openImageFloating : undefined}
              data-testid={`${dataTestId}-avatar`}
            >
              <AvatarImage src={src} alt={name || 'Sin nombre'} />
              <AvatarFallback className="bg-card-border text-text text-xs font-medium">
                {acronym || (name ? name.charAt(0).toUpperCase() : '?')}
              </AvatarFallback>
            </Avatar>
            <div>
              {/* 🎯 Condicional: con tooltip o sin tooltip */}
              {showTooltip ? (
                <TooltipProvider>
                  <TooltipSH>
                    <TooltipTrigger asChild>{nameContent}</TooltipTrigger>
                    <TooltipContent className="bg-background border-border border">
                      <p className="max-w-xs whitespace-normal text-text">
                        {name || 'Sin nombre'}
                      </p>
                    </TooltipContent>
                  </TooltipSH>
                </TooltipProvider>
              ) : (
                nameContent
              )}

              {code && (
                <Badge
                  variant="transparent-blue"
                  className="mt-0 px-2 py-[1px] text-xs w-fit uppercase font-normal rounded-full border-0"
                >
                  {code}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay flotante usando el componente separado */}
      <ProductImageOverlay
        isOpen={isImageFloatingOpen}
        onClose={closeImageFloating}
        src={src}
        name={name || 'Sin nombre'}
        code={code}
        data-testid={`${dataTestId}-overlay`}
      />
    </>
  );
};
